import React, { memo, useMemo, useState, createRef } from "react";
import Slider from "react-slick";
import "@qlik/qlik/src/assets/vendor/slick/slick.css";
import "@qlik/qlik/src/assets/vendor/slick/slick-theme.css";
import SliderCard from "@qlik/qlik/src/components/CardDeckContainer/Home_Slider/SliderCard";

/**
 * DeckSliderCardsDesktop component
 * @param {Object} props - Component props
 * @param {Array} props.cards - Array of card data
 */
const DeckSliderCardsDesktop = memo(({ cards = [] }) => {
    const [cardRefs, setCardRefs] = useState([]);
    const [groupRefs, setGroupRefs] = useState([]);

    /**
     * Groups the cards into chunks of 8
     */
    const groups = useMemo(() => {
        const chunkSize = 8;
        const data = [];
        for (let i = 0; i < cards.length; i += chunkSize) {
            const chunk = cards.slice(i, i + chunkSize);
            data.push(chunk);
        }

        // Create refs for each card and group
        setCardRefs(() =>
            Array(cards.length)
                .fill()
                .map(() => createRef())
        );

        setGroupRefs(() =>
            Array(data.length)
                .fill()
                .map(() => createRef())
        );

        return data;
    }, [cards]);

    /**
     * Handles hover event on a card
     * @param {number} gIndex - Group index
     * @param {number} cIndex - Card index within the group
     */
    const onHoverHandler = (gIndex, cIndex) => {
        const index = gIndex * 8 + cIndex;

        // Handle hover for first and fifth card in a group
        if (cIndex === 0 || cIndex === 4) {
            const next = cardRefs[index + 1];
            const after = cardRefs[index + 2];
            const last = cardRefs[index + 3];

            next.current.classList.add("move-right");
            after.current.classList.add("move-right");
            last.current.classList.add("move-right");

            groupRefs[gIndex].current.classList.add("entire-right");
            groupRefs[gIndex].current.classList.remove("entire-left");
        }

        // Handle hover for second and sixth card in a group
        if (cIndex === 1 || cIndex === 5) {
            const next = cardRefs[index + 1];
            const after = cardRefs[index + 2];

            next.current.classList.add("move-right");
            after.current.classList.add("move-right");

            groupRefs[gIndex].current.classList.remove("entire-left");
            groupRefs[gIndex].current.classList.remove("entire-right");
        }

        // Handle hover for third and seventh card in a group
        if (cIndex === 2 || cIndex === 6) {
            const prev = cardRefs[index - 1];
            const before = cardRefs[index - 2];

            prev.current.classList.add("move-left");
            before.current.classList.add("move-left");

            groupRefs[gIndex].current.classList.remove("entire-left");
            groupRefs[gIndex].current.classList.remove("entire-right");
        }

        // Handle hover for fourth and eighth card in a group
        if (cIndex === 3 || cIndex === 7) {
            const prev = cardRefs[index - 1];
            const before = cardRefs[index - 2];
            const first = cardRefs[index - 3];

            prev.current.classList.add("move-left");
            before.current.classList.add("move-left");
            first.current.classList.add("move-left");

            groupRefs[gIndex].current.classList.add("entire-left");
            groupRefs[gIndex].current.classList.remove("entire-right");
        }
    };

    /**
     * Handles hover removal event on a card
     * @param {number} gIndex - Group index
     * @param {number} cIndex - Card index within the group
     */
    const onHoverRemoveHandler = (gIndex, cIndex) => {
        const index = gIndex * 8 + cIndex;

        // Handle hover removal for first and fifth card in a group
        if (cIndex === 0 || cIndex === 4) {
            const next = cardRefs[index + 1];
            const after = cardRefs[index + 2];
            const last = cardRefs[index + 3];

            next.current.classList.remove("move-right");
            after.current.classList.remove("move-right");
            last.current.classList.remove("move-right");
        }

        // Handle hover removal for second and sixth card in a group
        if (cIndex === 1 || cIndex === 5) {
            const next = cardRefs[index + 1];
            const after = cardRefs[index + 2];

            next.current.classList.remove("move-right");
            after.current.classList.remove("move-right");
        }

        // Handle hover removal for third and seventh card in a group
        if (cIndex === 2 || cIndex === 6) {
            const prev = cardRefs[index - 1];
            const before = cardRefs[index - 2];

            prev.current.classList.remove("move-left");
            before.current.classList.remove("move-left");
        }

        // Handle hover removal for fourth and eighth card in a group
        if (cIndex === 3 || cIndex === 7) {
            const prev = cardRefs[index - 1];
            const before = cardRefs[index - 2];
            const first = cardRefs[index - 3];

            prev.current.classList.remove("move-left");
            before.current.classList.remove("move-left");
            first.current.classList.remove("move-left");
        }
    };

    // Render the component
    return (
        <div className="container">
            <div className="lg:-mx-[204px]">
                <Slider
                    className="w-full slider-cards slick-arrow-top-right"
                    infinite={false}
                    arrows
                    slidesToShow={1}
                    slidesToScroll={1}
                    duration={1000}
                >
                    {groups.map((group, gIndex) => (
                        <div key={gIndex}>
                            <div
                                className="grid grid-cols-4 gap-15 transition-all ease-out duration-1000"
                                ref={groupRefs[gIndex]}
                            >
                                {group.map((card, index) => {
                                    const isReverse = index % 4 > 1;
                                    return (
                                        <div
                                            key={`${gIndex}_${index}`}
                                            ref={cardRefs[gIndex * 8 + index]}
                                            className="lg:w-[279px] xl:w-87.5 relative transition-all ease-out duration-700"
                                            role="button"
                                            onMouseEnter={() => onHoverHandler(gIndex, index)}
                                            onMouseLeave={() => onHoverRemoveHandler(gIndex, index)}
                                        >
                                            <SliderCard
                                                card={card}
                                                key={card.id}
                                                type="desktop"
                                                className={`${isReverse ? "float-right" : ""}`}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </Slider>
            </div>
        </div>
    );
});

export default DeckSliderCardsDesktop;
